'use client';

import { Building2 } from 'lucide-react';

import { EntityList } from '@/components/ui/design-system';
import { CompanyEntity } from '@/components/ui/design-system/entities/company/CompanyEntity';
import {
  toCompanyEntityData,
  type CompanyEntityActions,
  type CompanyEntityData,
} from '@/components/ui/design-system/entities/company/CompanyEntity.types';
import type { Company } from '@/types/companies-types';
import { cn } from '@/lib/utils';

/**
 * Shared companies list — used by /companies (public), /dashboard/companies,
 * and /dashboard/companies/search. Mirrors the JobsList API.
 *
 * Cards mode uses EntityList + CompanyEntity with thin overlapping borders
 * (`gap-0 [&>*+*]:-mt-px`) and a single-column layout. Table mode mirrors
 * the existing dashboard table markup.
 */
export interface CompaniesListProps {
  companies: Company[];
  viewMode?: 'cards' | 'table';
  actions?: CompanyEntityActions;
  density?: 'comfortable' | 'compact';
  emptyState?: React.ReactNode;
  itemKeyPrefix?: string;
  onCardClick?: (company: CompanyEntityData) => void;
  onQuickView?: (company: CompanyEntityData) => void;
  onResearch?: (company: CompanyEntityData) => void;
  /** Optional per-card footer slot (e.g. custom action buttons). */
  renderFooter?: (company: CompanyEntityData) => React.ReactNode;
  cardsContainerClassName?: string;
  tableContainerClassName?: string;
}

const DEFAULT_ACTIONS: CompanyEntityActions = {
  research: true,
  quickView: true,
};

function formatEmployeeCount(count: number | null | undefined): string {
  if (!count) return '—';
  if (count >= 10000) return '10,000+';
  if (count >= 5000) return '5,000+';
  if (count >= 1000) return '1,000+';
  if (count >= 500) return '500+';
  return `${count}`;
}

function formatWebsiteDisplay(value?: string | null): string {
  if (!value) return '—';
  return value
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/$/, '');
}

function getLocation(company: Company): string {
  return company.locality || company.region || company.country_name || '—';
}

export function CompaniesList({
  companies,
  viewMode = 'cards',
  actions,
  density = 'comfortable',
  emptyState,
  itemKeyPrefix,
  onCardClick,
  onQuickView,
  onResearch,
  renderFooter,
  cardsContainerClassName,
  tableContainerClassName,
}: CompaniesListProps) {
  const resolvedActions = { ...DEFAULT_ACTIONS, ...actions };

  if (companies.length === 0) {
    return (
      <>
        {emptyState ?? (
          <div className="p-4 border border-border bg-card text-sm text-muted-foreground text-center">
            No companies found.
          </div>
        )}
      </>
    );
  }

  if (viewMode === 'table') {
    return (
      <div
        className={cn('overflow-x-auto border border-border bg-card', tableContainerClassName)}
        style={{ boxShadow: 'var(--shadow-warm-sm)' }}
      >
        <table className="w-full min-w-[760px] border-collapse">
          <thead className="bg-muted/40">
            <tr className="text-left text-overline text-muted-foreground">
              <th className="p-3 border-b border-border">Company</th>
              <th className="p-3 border-b border-border">Industry</th>
              <th className="p-3 border-b border-border">Location</th>
              <th className="p-3 border-b border-border">Employees</th>
              <th className="p-3 border-b border-border">Website</th>
            </tr>
          </thead>
          <tbody>
            {companies.map((company) => {
              const data = toCompanyEntityData(company);
              const rowKey = `${itemKeyPrefix ?? 'companies'}-${company.id}`;

              return (
                <tr
                  key={rowKey}
                  className="border-b border-border hover:bg-muted/30 transition-colors cursor-pointer"
                  onClick={() => onCardClick?.(data) ?? onQuickView?.(data)}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {company.square_logo_url ? (
                        <img
                          src={company.square_logo_url}
                          alt={company.company_name}
                          className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <span className="text-label text-foreground truncate">{company.company_name}</span>
                    </div>
                  </td>
                  <td className="p-3 text-body text-muted-foreground">{company.industry || '—'}</td>
                  <td className="p-3 text-body text-muted-foreground">{getLocation(company)}</td>
                  <td className="p-3 text-body text-muted-foreground">{formatEmployeeCount(company.employee_count)}</td>
                  <td className="p-3 text-body text-muted-foreground">{formatWebsiteDisplay(company.domain || company.website)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <EntityList
      mode="separated"
      listClassName={cn('gap-0 [&>*+*]:-mt-px', cardsContainerClassName)}
      items={companies}
      itemKey={(company) => `${itemKeyPrefix ?? 'companies'}-${company.id}`}
      renderItem={(company) => {
        const data = toCompanyEntityData(company);
        return (
          <CompanyEntity
            company={data}
            layout="card"
            density={density}
            actions={resolvedActions}
            onCardClick={onCardClick}
            onQuickView={onQuickView}
            onResearch={onResearch}
            footer={renderFooter?.(data)}
          />
        );
      }}
    />
  );
}

export default CompaniesList;
